import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Text,
} from '@mantine/core';

import {DnsZone} from '../types';
import {DelegationBadge} from '../components/DelegationBadge';
import {
    providerColor,
    providerLabel,
} from '../providerLabels';
import config from '../config';

// ⚠️ PROVIDER IS A COLUMN, NOT A GROUPING.
//
// A portfolio spread across three providers is ONE list with provenance as an
// attribute — not three tables, not a grouped table, not a provider switcher.
// That is the point of the neutral layer: `provider` is the only field in the
// schema that reveals where a zone came from, and it earns a column rather than
// restructuring the page.
export const useZonesTableColumns = () => {

    // EVERY column is sortable (the module checklist is explicit), through the
    // shared comparator.
    const columns: DataTableColumn<DnsZone>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            width: '1fr',
            render: (item: DnsZone) => (
                // ⚠️ The link carries `connectionId`, because `/zones/:zoneId`
                // alone cannot address a zone — getDnsZone needs both. See
                // hooks/useZone for the full resolution order.
                <NavLink
                    to={`${config.basePath}/${encodeURIComponent(item.id)}?connectionId=${encodeURIComponent(item.connectionId)}`}
                >
                    <Text size={'sm'}>{item.name}</Text>
                </NavLink>
            ),
        },
        {
            accessor: 'provider',
            sortable: true,
            title: I18n.get('Provider'),
            width: '140px',
            render: (item: DnsZone) => (
                <Badge variant={'light'} color={providerColor(item.provider)}>
                    {providerLabel(item.provider)}
                </Badge>
            ),
        },
        {
            accessor: 'appearsDelegated',
            sortable: true,
            title: I18n.get('Delegation'),
            width: '160px',
            render: (item: DnsZone) => <DelegationBadge zone={item}/>,
        },
        {
            accessor: 'isPrivate',
            sortable: true,
            title: I18n.get('Visibility'),
            width: '120px',
            // `isPrivate` is `Boolean!` — non-null, so unlike the hosted-zones
            // equivalent there is no third state to render here.
            render: (item: DnsZone) => (
                <Badge
                    variant={'light'}
                    color={item.isPrivate ? 'orange' : 'blue'}
                >
                    {item.isPrivate ? I18n.get('Private') : I18n.get('Public')}
                </Badge>
            ),
        },
        {
            accessor: 'nameservers',
            sortable: true,
            title: I18n.get('Nameservers'),
            width: '110px',
            // The COUNT, not the list: four hostnames per row would drown the
            // table. The full delegation set is on the detail page.
            render: (item: DnsZone) => (
                <Text size={'sm'}>{item.nameservers?.length ?? 0}</Text>
            ),
        },
        // ⚠️ THERE IS DELIBERATELY NO ZONE ID COLUMN.
        //
        // `DnsZone.id` is OPAQUE — Flexi's own identifier, minted by the
        // adapter, never the provider's. Putting it in front of an operator
        // invites them to paste it into the AWS console or a Cloudflare
        // dashboard, where it means nothing. The hosted-zones module shows its
        // zone id precisely because that one IS the Route 53 id.
    ];

    return {
        columns,
    };
};
